import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES, readImagePayload, ImageUploadPayload } from './imageFile';

interface GalleryInputProps {
  label?: string;
  /** Current ordered list of image URLs. */
  value: string[];
  /** Called with the new ordered list whenever an image is added or removed. */
  onChange: (urls: string[]) => void;
  /** Uploads a single picked file and resolves to its public URL. */
  onUpload: (payload: ImageUploadPayload) => Promise<string>;
  /** Maximum number of images (default 12). */
  max?: number;
  helpText?: string;
  disabled?: boolean;
  onError?: (message: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 0.6rem;
`;

const Tile = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: ${(p) => p.theme.radius};
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.error};
  }
`;

const AddTile = styled.button<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  aspect-ratio: 1 / 1;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  color: ${(p) => p.theme.textMuted};
  font-size: 0.72rem;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: ${(p) => (p.$disabled ? p.theme.border : p.theme.primary)};
    color: ${(p) => (p.$disabled ? p.theme.textMuted : p.theme.primary)};
  }
`;

const Spinner = styled(Loader2)`
  animation: ku-spin 0.8s linear infinite;
  @keyframes ku-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const HelpText = styled.small<{ $isError?: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$isError ? p.theme.error : p.theme.textMuted)};
`;

const HiddenInput = styled.input`
  display: none;
`;

/**
 * Multi-image gallery picker for the store profile. Uploads each picked file
 * and keeps an ordered list of URLs. Uses only design-system tokens.
 */
export const GalleryInput: React.FC<GalleryInputProps> = ({
  label,
  value,
  onChange,
  onUpload,
  max = 12,
  helpText,
  disabled,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const remaining = Math.max(0, max - value.length);
  const canAdd = !disabled && !uploading && remaining > 0;

  const fail = (message: string) => {
    setLocalError(message);
    onError?.(message);
  };

  const openPicker = () => {
    if (!canAdd) return;
    inputRef.current?.click();
  };

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    /*
      Passar do teto avisa, e não envia nada.

      Antes daqui saía um `files.slice(0, remaining)` silencioso: quem tinha 8
      fotos e escolhia 5 via 2 sumirem sem uma linha de explicação, e ia
      procurar o problema no navegador. O teto é regra de produto - e regra que
      o usuário não vê aplicada parece defeito. Recusar a seleção inteira
      também é mais previsível do que adivinhar QUAIS das cinco ele quis:
      escolher por ele seria outro palpite.
    */
    if (files.length > remaining) {
      fail(
        remaining === 1
          ? 'Falta espaço para 1 foto. Selecione uma só.'
          : `Falta espaço para ${remaining} fotos. Selecione até ${remaining}.`
      );
      return;
    }

    if (files.some((f) => f.size > MAX_IMAGE_BYTES)) {
      fail('Cada imagem deve ter no máximo 5 MB.');
      return;
    }

    setLocalError(null);
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of files) {
        const payload = await readImagePayload(file);
        // eslint-disable-next-line no-await-in-loop
        uploaded.push(await onUpload(payload));
      }
      onChange([...value, ...uploaded]);
    } catch {
      fail('Não foi possível enviar uma ou mais imagens.');
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Container>
      {label && <Label>{label}</Label>}
      <Grid>
        {value.map((url, index) => (
          <Tile key={`${url}-${index}`}>
            <Thumb src={url} alt={`Imagem ${index + 1}`} />
            {!disabled && (
              <RemoveButton type="button" title="Remover" onClick={() => removeAt(index)}>
                <X size={14} />
              </RemoveButton>
            )}
          </Tile>
        ))}
        {remaining > 0 && (
          <AddTile type="button" $disabled={!canAdd} onClick={openPicker}>
            {uploading ? (
              <Spinner size={20} />
            ) : (
              <>
                <ImagePlus size={20} />
                Adicionar
              </>
            )}
          </AddTile>
        )}
      </Grid>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        onChange={handleFiles}
        disabled={!canAdd}
      />
      {(localError || helpText) && (
        <HelpText $isError={!!localError}>
          {localError || helpText || `${value.length}/${max} imagens`}
        </HelpText>
      )}
    </Container>
  );
};

export default GalleryInput;
